<template>
  <div @click="initial">
    <!-- The contenteditable area (no styling by default) -->
    <editor-content :editor="editor" />

    <!-- Example of “headless” data access -->
    <textarea
      v-model="html"
      rows="6"
      style="width:100%; margin-top:1rem;"
      readonly
    />
  </div>
</template>

<script>
import { Editor, Mark } from '@tiptap/core'
import { EditorContent } from '@tiptap/vue-2'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Underline from '@tiptap/extension-underline'
import Strike from '@tiptap/extension-strike'
import Heading from '@tiptap/extension-heading'
import ListItem from '@tiptap/extension-list-item'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import Link from '@tiptap/extension-link'
import History from '@tiptap/extension-history'
import Placeholder from '@tiptap/extension-placeholder'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import TiptapFontFamily from '@builder/plugins/TiptapFontFamily'
import FontWeight from '@builder/plugins/TiptapFontWeight'
import FontWeight2 from '@builder/plugins/TiptapFontWeight2'
import FontWeight3 from '@builder/plugins/TiptapFontWeight3'
import FontSize from '@builder/plugins/TiptapFontSize'
import BackgroundColor from '@builder/plugins/TiptapBackgroundColor'
import CustomLink from '@builder/plugins/TiptapCustomLink'
import TextGradientColor from '@/plugins/TiptapTextGradientColor'
import TiptapGlobalVariable from '@/plugins/TiptapGlobalVariable'

// Custom mark: fontfamily (applies a font-family CSS style)
const FontFamily = Mark.create({
  name: 'fontfamily',

  addAttributes () {
    return {
      family: {
        default: '',
        parseHTML: element => {
          const value = element.style.fontFamily || element.getAttribute('data-font-family') || ''
          return value || ''
        },
        renderHTML: attrs => {
          const fam = attrs.family || ''
          if (!fam) {
            return {}
          }
          const needsQuotes = /[\s,]/.test(fam)
          const safe = needsQuotes ? `"${String(fam).replace(/\\"/g, '\\"')}"` : fam
          return {
            style: `font-family: ${safe}`
          }
        }
      }
    }
  },

  parseHTML () {
    return [
      {
        style: 'font-family'
      }
    ]
  },

  renderHTML ({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0]
  },

  addCommands () {
    return {
      fontFamily: ({ family }) => ({ state, dispatch }) => {
        const { from, to } = state.selection
        const type = this.type
        let tr = state.tr.removeMark(from, to, type)
        if (family) {
          tr = tr.addMark(from, to, type.create({ family }))
        }
        if (dispatch) {
          dispatch(tr.scrollIntoView())
        }
        return true
      },
      setFontFamily: family => ({ state, dispatch }) => {
        const { from, to } = state.selection
        const type = this.type
        let tr = state.tr.removeMark(from, to, type)
        if (family) {
          tr = tr.addMark(from, to, type.create({ family }))
        }
        if (dispatch) {
          dispatch(tr.scrollIntoView())
        }
        return true
      },
      clearFontFamily: () => ({ state, dispatch }) => {
        const { from, to } = state.selection
        const type = this.type
        const tr = state.tr.removeMark(from, to, type)
        if (dispatch) {
          dispatch(tr)
        }
        return true
      }
    }
  }
})

// Custom mark: text_transform (uppercase | capitalize | lowercase)
const TextTransform = Mark.create({
  name: 'text_transform',

  addAttributes () {
    return {
      type: {
        default: null // 'uppercase' | 'capitalize' | 'lowercase'
      }
    }
  },

  parseHTML () {
    return [
      {
        style: 'text-transform',
        getAttrs: value => {
          const v = String(value).toLowerCase()
          if (v === 'uppercase' || v === 'capitalize' || v === 'lowercase') {
            return { type: v }
          }
          return false
        }
      }
    ]
  },

  renderHTML ({ HTMLAttributes }) {
    const t = HTMLAttributes.type
    if (!t) {
      return ['span', 0]
    }
    return ['span', { style: `text-transform: ${t}` }, 0]
  },

  addCommands () {
    const toggle = attrs => ({ state, dispatch }) => {
      const markType = this.type
      const { from, to, empty } = state.selection
      let shouldUnset = false

      state.doc.nodesBetween(from, empty ? to + 1 : to, node => {
        if (!node.marks) {
          return
        }
        const found = node.marks.find(m => m.type === markType)
        if (
          found &&
          ((!attrs.type && found.attrs.type) ||
            (attrs.type && found.attrs.type === attrs.type))
        ) {
          shouldUnset = true
        }
      })

      if (shouldUnset) {
        const tr = state.tr.removeMark(from, to, markType)
        if (dispatch) {
          dispatch(tr)
        }
        return true
      }

      let tr = state.tr.removeMark(from, to, markType)
      if (attrs.type) {
        tr = tr.addMark(from, to, markType.create(attrs))
      }
      if (dispatch) {
        dispatch(tr.scrollIntoView())
      }
      return true
    }

    return {
      textTransform: ({ textTransform }) => toggle({ type: textTransform }),
      uppercase: () => toggle({ type: 'uppercase' }),
      capitalize: () => toggle({ type: 'capitalize' }),
      lowercase: () => toggle({ type: 'lowercase' }),
      clearTextTransform: () => toggle({ type: null })
    }
  }
})

export default {
  name: 'ContentEditableV3',
  components: { EditorContent },
  props: {
    value: { type: String, default: '<p></p>' },
    placeholder: { type: String, default: 'Start typing…' }
  },
  data () {
    return {
      editor: new Editor({
        extensions: [
          Document,
          Paragraph,
          Text,
          Heading.configure({ levels: [1, 2, 3, 4, 5, 6] }),
          FontFamily,
          TextTransform,
          Bold,
          Italic,
          Underline,
          Strike,
          BulletList,
          OrderedList,
          ListItem,
          Link.configure({ openOnClick: false }),
          History,
          Placeholder.configure({
            placeholder: this.placeholder,
            showOnlyWhenEditable: true,
            showOnlyCurrent: false
          }),
          TiptapFontFamily,
          FontWeight,
          FontWeight2,
          FontWeight3,
          FontSize,
          BackgroundColor,
          CustomLink,
          TextGradientColor,
          TiptapGlobalVariable
        ],
        content: this.value,
        onUpdate: ({ editor }) => {
          this.html = editor.getHTML()
          this.$emit('input', this.html)
        }
      }),
      html: this.value
    }
  },
  mounted () {
    VEvent.listen(this.uid, () => {
      this.initial()
    })
  },
  beforeDestroy () {
    if (this.editor) this.editor.destroy()
  },
  methods: {
    initial () {
      this.$store.commit('editor/SET_TIPTAP', this.editor)
    }
  },
  watch: {
    value (next) {
      if (this.editor && next !== this.html) {
        this.editor.setContent(next, false)
      }
    }
  }
}
</script>
